import { useEffect, useRef } from 'react';
const stack = [];
const selector = 'button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href],summary,[tabindex="0"]';
// All modal surfaces portal to body. Inert siblings, trap focus and restore the opener.
export function useModalFocus(ref, onClose, enabled = true) {
  const close = useRef(onClose); close.current = onClose;
  useEffect(() => {
    if (!enabled || !ref.current) return;
    const panel = ref.current, opener = document.activeElement;
    let host = panel; while (host.parentElement && host.parentElement !== document.body) host = host.parentElement;
    const saved = [...document.body.children].filter(n => n !== host && n.tagName !== 'SCRIPT').map(n => [n, n.inert]);
    saved.forEach(([n]) => { n.inert = true; });
    stack.push(panel);
    const items = () => [...panel.querySelectorAll(selector)].filter(n => {
      if(n.tabIndex < 0 || !n.getClientRects().length || getComputedStyle(n).visibility === 'hidden' || n.closest('[inert]')) return false;
      // Closed details can retain child layout boxes. Only their summary is tabbable.
      for(let parent=n.parentElement;parent && parent!==panel;parent=parent.parentElement) {
        if(parent.tagName==='DETAILS' && !parent.open && !parent.querySelector(':scope > summary')?.contains(n)) return false;
      }
      return true;
    });
    (panel.querySelector('[data-autofocus]') || items()[0] || panel).focus();
    function key(e) {
      if (stack.at(-1) !== panel) return;
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); close.current?.(); }
      if (e.key !== 'Tab') return;
      const all = items(), first = all[0], last = all.at(-1);
      if (!all.length) { e.preventDefault(); panel.focus(); }
      else if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || document.activeElement === panel)) { e.preventDefault(); first.focus(); }
    }
    function focus(e) { if (stack.at(-1) === panel && !panel.contains(e.target)) (items()[0] || panel).focus(); }
    document.addEventListener('keydown', key, true); document.addEventListener('focusin', focus);
    return () => {
      stack.splice(stack.indexOf(panel), 1); saved.forEach(([n, inert]) => { n.inert = inert; });
      document.removeEventListener('keydown', key, true); document.removeEventListener('focusin', focus);
      if (opener?.isConnected && !opener.closest('[inert]')) opener.focus();
    };
  }, [enabled, ref]);
}
